"use client";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { CONSENT_OPEN_EVENT, CONSENT_SET_EVENT, categoriesFor, emitConsentChange, isGranted, newConsentId, readStoredConsent, serviceIndex, validConsent, writeStoredConsent } from "./client/store";
import { applyIntegrations, revokeIntegrations, trackPageView } from "./client/vendors";
import { clearServices } from "./client/clear";
import { OPTIONAL_CATEGORIES, type ConsentClientConfig, type OptionalCategory, type StoredConsent } from "./types";

/**
 * Public-site consent runtime (docs/cookie-consent-plan.md §10): the AP-proof
 * banner (accept, reject and customise as equal buttons on the first layer),
 * the preferences dialog, the always-available settings button, script
 * activation after consent, withdrawal (vendor signal + cookie clean-up +
 * reload) and the proof-of-consent beacon. Rendered once per public page by
 * the root layout with a config that is identical for every visitor.
 */

type Action = "accept_all" | "reject_all" | "custom" | "withdraw" | "gpc" | "embed";
type Source = "banner" | "preferences" | "floating" | "policy_page" | "embed" | "gpc";

export { openConsentPreferences } from "./client/store";

function beacon(config: ConsentClientConfig, state: StoredConsent, action: Action, source: Source) {
  const body = JSON.stringify({ consentId: state.id, revision: state.rev, locale: config.locale, action, source, categories: state.c, services: state.s || {}, path: location.pathname });
  try {
    if (!navigator.sendBeacon?.("/api/consent", new Blob([body], { type: "application/json" }))) {
      void fetch("/api/consent", { method: "POST", body, keepalive: true, headers: { "content-type": "application/json" } });
    }
  } catch {
    // logging must never block the visitor's choice
  }
}

/** Run inert <script type="text/plain" data-cms-service> nodes and guard-blocked nodes whose service is now granted. */
function releaseGated(config: ConsentClientConfig, state: StoredConsent | null) {
  for (const node of document.querySelectorAll<HTMLScriptElement>("script[type='text/plain'][data-cms-service]:not([data-cms-done])")) {
    const service = node.dataset.cmsService!;
    if (!isGranted(service, config, state)) continue;
    node.dataset.cmsDone = "1";
    const script = document.createElement("script");
    for (const attribute of node.attributes) {
      if (["type", "data-cms-service", "data-cms-done", "data-src", "data-cms-blocked-src"].includes(attribute.name)) continue;
      script.setAttribute(attribute.name, attribute.value);
    }
    const src = node.dataset.src || node.dataset.cmsBlockedSrc;
    if (src) script.src = src;
    else script.text = node.text;
    script.setAttribute("data-cms-allowed", "");
    node.after(script);
  }
  const blocked = (window as Window & { __cmsBlocked?: Element[] }).__cmsBlocked || [];
  for (const node of [...blocked]) {
    const service = node.getAttribute("data-cms-service");
    const src = node.getAttribute("data-cms-blocked-src");
    if (!service || !src || !isGranted(service, config, state)) continue;
    blocked.splice(blocked.indexOf(node), 1);
    node.setAttribute("data-cms-allowed", "");
    if (node.tagName === "SCRIPT") {
      const script = document.createElement("script");
      script.src = src;
      script.setAttribute("data-cms-allowed", "");
      node.replaceWith(script);
    } else node.setAttribute("src", src);
  }
}

/** Names of first-party cookies/storage keys nobody declared (sampled, names only; never values). */
function reportUnknown(config: ConsentClientConfig, beforeConsent: boolean) {
  if (Math.random() > 0.05) return;
  const known = new Set<string>(["cms_consent", "cms_session"]);
  const prefixes: string[] = [];
  for (const rules of Object.values(config.clear)) { rules.cookies.forEach((name) => known.add(name)); rules.storage.forEach((name) => known.add(name)); prefixes.push(...rules.prefixes); }
  for (const category of config.categories) for (const service of category.services) for (const cookie of service.cookies) {
    if (cookie.name.endsWith("*")) prefixes.push(cookie.name.slice(0, -1));
    else known.add(cookie.name);
  }
  const unknown = (name: string) => name && !known.has(name) && !prefixes.some((prefix) => name.startsWith(prefix)) && !name.startsWith("__next") && !name.startsWith("next-");
  const cookies = document.cookie.split(";").map((part) => part.split("=")[0].trim()).filter(unknown);
  let storage: string[] = [];
  try {
    storage = Object.keys(localStorage).filter((key) => unknown(key) && !key.startsWith("cms-"));
  } catch {
    storage = [];
  }
  if (!cookies.length && !storage.length) return;
  navigator.sendBeacon?.("/api/privacy/observations", new Blob([JSON.stringify({ cookies, storage, beforeConsent, path: location.pathname })], { type: "application/json" }));
}

const COOKIE_ICON = <svg aria-hidden="true" viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5Zm-3.5 7a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm2 6a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm6 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z" /></svg>;

export function ConsentRuntime({ config }: { config: ConsentClientConfig }) {
  const [state, setState] = useState<StoredConsent | null>(null);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [gpcNotice, setGpcNotice] = useState(false);
  const [ready, setReady] = useState(false);
  const pathname = usePathname();
  const firstPath = useRef(true);
  const optional = config.categories.some((category) => category.id !== "necessary");
  const t = config.texts;

  const apply = useCallback((next: StoredConsent | null) => {
    window.__cmsConsentState = next;
    applyIntegrations(config, next);
    releaseGated(config, next);
    emitConsentChange(next);
  }, [config]);

  const choose = useCallback(async (action: Action, categories: StoredConsent["c"], source: Source, services?: Record<string, boolean>) => {
    const previous = validConsent(readStoredConsent(), config);
    const next: StoredConsent = { v: 1, id: previous?.id || newConsentId(), rev: config.revision, ts: Date.now(), c: categories, s: services ?? previous?.s };
    const revoked = [...serviceIndex(config).keys()].filter((id) => isGranted(id, config, previous) && !isGranted(id, config, next));
    writeStoredConsent(next, config.expiryDays);
    beacon(config, next, revoked.length && action === "custom" ? "withdraw" : action, source);
    setState(next);
    setBannerOpen(false);
    setDialogOpen(false);
    document.documentElement.setAttribute("data-consent", "set");
    if (revoked.length && previous) {
      // Running scripts can't be unloaded: inform vendors, delete their cookies, reload.
      revokeIntegrations(config, revoked);
      await clearServices(config, revoked);
      location.reload();
      return;
    }
    apply(next);
  }, [apply, config]);

  useEffect(() => {
    window.__cmsConsentConfig = config;
    const stored = validConsent(readStoredConsent(), config);
    setState(stored);
    window.cmsConsent = {
      get: () => validConsent(readStoredConsent(), config),
      granted: (id) => isGranted(id, config, validConsent(readStoredConsent(), config)),
      open: () => setDialogOpen(true),
      onChange: (callback) => {
        const listener = (event: Event) => callback((event as CustomEvent<StoredConsent | null>).detail);
        window.addEventListener("cms:consent", listener);
        return () => window.removeEventListener("cms:consent", listener);
      },
    };
    // Search-engine crawlers get the clean page: no banner. They never accept, so nothing gated loads for them.
    const crawler = /bot\b|crawl|spider|slurp|bingpreview|facebookexternalhit|embedly|whatsapp|telegrambot|google-inspectiontool/i.test(navigator.userAgent);
    if (!stored && config.bannerNeeded && !crawler) {
      const gpc = (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
      if (gpc && config.honorGpc) {
        void choose("gpc", categoriesFor("reject_all", config), "gpc");
        setGpcNotice(true);
      } else setBannerOpen(true);
    }
    apply(stored);
    setReady(true);
    const timer = window.setTimeout(() => reportUnknown(config, !stored), 5000);
    const open = () => setDialogOpen(true);
    const set = (event: Event) => {
      const detail = (event as CustomEvent<{ action?: string; source?: Source }>).detail || {};
      if (detail.action !== "accept_all" && detail.action !== "reject_all") return;
      void choose(detail.action, categoriesFor(detail.action, config), detail.source === "policy_page" ? "policy_page" : "preferences");
    };
    window.addEventListener(CONSENT_OPEN_EVENT, open);
    window.addEventListener(CONSENT_SET_EVENT, set);
    return () => { window.clearTimeout(timer); window.removeEventListener(CONSENT_OPEN_EVENT, open); window.removeEventListener(CONSENT_SET_EVENT, set); };
    // The config is fixed for the page; run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (firstPath.current) { firstPath.current = false; return; }
    trackPageView(config, validConsent(readStoredConsent(), config));
    releaseGated(config, validConsent(readStoredConsent(), config));
  }, [pathname, config]);

  if (!ready) return null;
  const colors = config.colors || {};
  const style = `
.cms-consent{--cc-bg:${colors.background || "#ffffff"};--cc-text:${colors.text || "#1f2328"};--cc-btn:${colors.button || "#1f2328"};--cc-btn-text:${colors.buttonText || "#ffffff"};font:15px/1.5 system-ui,-apple-system,"Segoe UI",sans-serif;color:var(--cc-text)}
.cms-consent *{box-sizing:border-box}
.cms-consent-banner{position:fixed;z-index:2147483000;background:var(--cc-bg);color:var(--cc-text);box-shadow:0 -4px 24px rgba(0,0,0,.18);padding:20px;outline:none;max-height:80vh;overflow:auto}
.cms-consent-banner[data-layout=bar]{left:0;right:0;bottom:0}
.cms-consent-banner[data-layout=box]{left:16px;bottom:16px;max-width:440px;border-radius:12px}
.cms-consent-banner[data-layout=center]{left:50%;top:50%;transform:translate(-50%,-50%);max-width:560px;width:calc(100% - 32px);border-radius:12px;box-shadow:0 8px 40px rgba(0,0,0,.3)}
.cms-consent-inner{max-width:1100px;margin:0 auto;display:grid;gap:12px}
.cms-consent h2{margin:0;font-size:18px;line-height:1.3;color:inherit}
.cms-consent p{margin:0}
.cms-consent a{color:inherit;text-decoration:underline}
.cms-consent-actions{display:flex;flex-wrap:wrap;gap:8px}
.cms-consent-btn{flex:1 1 160px;min-height:44px;padding:10px 16px;border:2px solid var(--cc-btn);border-radius:8px;background:var(--cc-btn);color:var(--cc-btn-text);font:inherit;font-weight:600;cursor:pointer}
.cms-consent-btn:focus-visible,.cms-consent-fab:focus-visible,.cms-consent-switch:focus-visible,.cms-consent-close:focus-visible{outline:3px solid #2563eb;outline-offset:2px}
.cms-consent-fab{position:fixed;z-index:2147482999;bottom:16px;width:44px;height:44px;border-radius:50%;border:0;background:var(--cc-btn);color:var(--cc-btn-text);display:grid;place-items:center;cursor:pointer;box-shadow:0 2px 10px rgba(0,0,0,.25)}
.cms-consent-fab[data-position=left]{left:16px}.cms-consent-fab[data-position=right]{right:16px}
.cms-consent-backdrop{position:fixed;inset:0;z-index:2147483001;background:rgba(0,0,0,.5);display:grid;place-items:center;padding:16px}
.cms-consent-dialog{background:var(--cc-bg);color:var(--cc-text);border-radius:12px;max-width:640px;width:100%;max-height:calc(100vh - 32px);overflow:auto;padding:24px;display:grid;gap:16px}
.cms-consent-head{display:flex;justify-content:space-between;gap:12px;align-items:flex-start}
.cms-consent-close{border:0;background:transparent;color:inherit;font-size:26px;line-height:1;cursor:pointer;min-width:44px;min-height:44px}
.cms-consent-cat{border-top:1px solid rgba(127,127,127,.35);padding-top:14px;display:grid;gap:8px}
.cms-consent-cat-head{display:flex;justify-content:space-between;align-items:center;gap:12px}
.cms-consent-cat h3{margin:0;font-size:16px}
.cms-consent-switch{position:relative;width:52px;height:30px;border-radius:15px;border:2px solid var(--cc-btn);background:transparent;cursor:pointer;flex:none}
.cms-consent-switch::after{content:"";position:absolute;top:3px;left:3px;width:20px;height:20px;border-radius:50%;background:var(--cc-btn);transition:transform .15s}
.cms-consent-switch[aria-checked=true]{background:var(--cc-btn)}
.cms-consent-switch[aria-checked=true]::after{transform:translateX(22px);background:var(--cc-btn-text)}
.cms-consent-switch:disabled{opacity:.6;cursor:not-allowed}
.cms-consent details{font-size:14px}.cms-consent summary{cursor:pointer}
.cms-consent table{width:100%;border-collapse:collapse;font-size:13px;margin-top:6px}.cms-consent th,.cms-consent td{text-align:left;padding:4px 6px;border-bottom:1px solid rgba(127,127,127,.25);vertical-align:top}
.cms-consent-small{font-size:13px;opacity:.85}
@media (prefers-reduced-motion:reduce){.cms-consent-switch::after{transition:none}}`;

  return <div className="cms-consent" lang={config.locale}>
    <style>{style}</style>
    {bannerOpen && <Banner config={config} onChoose={(action) => void choose(action, categoriesFor(action, config), "banner")} onCustomise={() => { setBannerOpen(false); setDialogOpen(true); }} />}
    {gpcNotice && !dialogOpen && <div className="cms-consent-banner" data-layout="box" role="status"><div className="cms-consent-inner"><p>{t.gpcNotice}</p><div className="cms-consent-actions"><button type="button" className="cms-consent-btn" onClick={() => { setGpcNotice(false); setDialogOpen(true); }}>{t.gpcChange}</button><button type="button" className="cms-consent-btn" onClick={() => setGpcNotice(false)}>{t.close}</button></div></div></div>}
    {dialogOpen && <Preferences config={config} state={state} onClose={() => setDialogOpen(false)} onSave={(categories, action) => void choose(action, categories, bannerOpen ? "banner" : "preferences")} />}
    {config.floatingButton && optional && !bannerOpen && !dialogOpen && (
      <button type="button" className="cms-consent-fab" data-cms-consent-settings="" data-position={config.floatingPosition} aria-label={t.floatingLabel} title={t.floatingLabel} onClick={() => setDialogOpen(true)}>{COOKIE_ICON}</button>
    )}
  </div>;
}

function Banner({ config, onChoose, onCustomise }: { config: ConsentClientConfig; onChoose: (action: "accept_all" | "reject_all") => void; onCustomise: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const t = config.texts;
  useEffect(() => {
    // Keep focused content visible above the fixed banner (WCAG 2.4.11).
    const update = () => { document.documentElement.style.scrollPaddingBottom = config.layout === "bar" && ref.current ? `${ref.current.offsetHeight + 16}px` : ""; };
    update();
    window.addEventListener("resize", update);
    ref.current?.focus({ preventScroll: true });
    return () => { window.removeEventListener("resize", update); document.documentElement.style.scrollPaddingBottom = ""; };
  }, [config.layout]);
  return <div ref={ref} className="cms-consent-banner" data-layout={config.layout} role="region" aria-labelledby={titleId} tabIndex={-1}>
    <div className="cms-consent-inner">
      <h2 id={titleId}>{t.title}</h2>
      <p>{t.body}{config.policyUrl && <> <a href={config.policyUrl}>{t.policyLink}</a></>}</p>
      <div className="cms-consent-actions">
        <button type="button" className="cms-consent-btn" onClick={() => onChoose("accept_all")}>{t.acceptAll}</button>
        <button type="button" className="cms-consent-btn" onClick={() => onChoose("reject_all")}>{t.rejectAll}</button>
        <button type="button" className="cms-consent-btn" onClick={onCustomise}>{t.preferences}</button>
      </div>
    </div>
  </div>;
}

function Preferences({ config, state, onClose, onSave }: {
  config: ConsentClientConfig; state: StoredConsent | null; onClose: () => void;
  onSave: (categories: StoredConsent["c"], action: "accept_all" | "reject_all" | "custom") => void;
}) {
  const t = config.texts;
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<Element | null>(null);
  const initial = Object.fromEntries(OPTIONAL_CATEGORIES.map((category) => {
    const group = config.categories.find((item) => item.id === category);
    const interest = !!group?.services.length && group.services.every((service) => service.basis !== "consent");
    return [category, state ? state.c[category] ?? interest : interest];
  })) as Record<OptionalCategory, boolean>;
  const [choice, setChoice] = useState(initial);
  useEffect(() => {
    returnFocus.current = document.activeElement;
    const focusable = () => [...(dialog.current?.querySelectorAll<HTMLElement>("button:not([disabled]), a[href], summary") || [])];
    focusable()[0]?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); onClose(); }
      if (event.key !== "Tab") return;
      const items = focusable();
      if (!items.length) return;
      if (event.shiftKey && document.activeElement === items[0]) { event.preventDefault(); items.at(-1)!.focus(); }
      else if (!event.shiftKey && document.activeElement === items.at(-1)) { event.preventDefault(); items[0].focus(); }
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      (returnFocus.current as HTMLElement | null)?.focus?.();
    };
  }, [onClose]);
  const present = config.categories.filter((category) => category.id !== "necessary").map((category) => category.id as OptionalCategory);
  const all = (value: boolean) => Object.fromEntries(present.map((category) => [category, value]));
  return <div className="cms-consent-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <div ref={dialog} className="cms-consent-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="cms-consent-head">
        <h2 id={titleId}>{t.dialogTitle}</h2>
        <button type="button" className="cms-consent-close" aria-label={t.close} onClick={onClose}>×</button>
      </div>
      <p>{t.dialogIntro}{config.policyUrl && <> <a href={config.policyUrl}>{t.policyLink}</a></>}</p>
      {state && <p className="cms-consent-small">{t.consentId}: <code>{state.id}</code> · {t.consentDate} {new Date(state.ts).toLocaleString(config.locale)}</p>}
      {config.categories.map((category) => {
        const optionalCategory = category.id !== "necessary";
        const onlyInterest = optionalCategory && category.services.length > 0 && category.services.every((service) => service.basis !== "consent");
        const checked = optionalCategory ? !!choice[category.id as OptionalCategory] : true;
        const labelId = `${titleId}-${category.id}`;
        return <section key={category.id} className="cms-consent-cat">
          <div className="cms-consent-cat-head">
            <h3 id={labelId}>{t[`category.${category.id}`]}</h3>
            {optionalCategory
              ? <button type="button" role="switch" className="cms-consent-switch" aria-checked={checked} aria-labelledby={labelId} aria-describedby={`${labelId}-help`}
                onClick={() => setChoice({ ...choice, [category.id]: !checked })} />
              : <span className="cms-consent-small">{t.alwaysOn}</span>}
          </div>
          <p id={`${labelId}-help`} className="cms-consent-small">{t[`categoryHelp.${category.id}`]}{onlyInterest ? ` ${t["basis.legitimate_interest"]}.` : ""}</p>
          {category.services.length > 0 && <details>
            <summary>{t.services} ({category.services.length})</summary>
            {category.services.map((service) => <div key={service.id} style={{ marginTop: 8 }}>
              <strong>{service.name}</strong>{service.provider && <> · {t.provider}: {service.provider}</>}<br />
              <span className="cms-consent-small">{service.purpose} ({t[`basis.${service.basis}`]})</span>
              {service.cookies.length > 0 && <table><thead><tr><th scope="col">Cookie</th><th scope="col">{t.retention}</th></tr></thead><tbody>
                {service.cookies.map((cookie) => <tr key={cookie.name}><td>{cookie.name}{cookie.domain ? ` (${t.thirdParty.replace("{domain}", cookie.domain)})` : ""}</td><td>{cookie.retention}</td></tr>)}
              </tbody></table>}
            </div>)}
          </details>}
        </section>;
      })}
      <div className="cms-consent-actions">
        <button type="button" className="cms-consent-btn" onClick={() => onSave(all(false), "reject_all")}>{t.rejectAll}</button>
        <button type="button" className="cms-consent-btn" onClick={() => onSave(Object.fromEntries(present.map((category) => [category, !!choice[category]])), "custom")}>{t.save}</button>
        <button type="button" className="cms-consent-btn" onClick={() => onSave(all(true), "accept_all")}>{t.acceptAll}</button>
      </div>
    </div>
  </div>;
}
